export type TokenPair = { accessToken: string; refreshToken: string };

export function serializeTokenPair(tokens: TokenPair): string {
  return JSON.stringify({ accessToken: tokens.accessToken, refreshToken: tokens.refreshToken });
}

export function parseTokenPair(raw: string): TokenPair | null {
  try {
    const parsed = JSON.parse(raw) as Partial<TokenPair>;
    if (typeof parsed.accessToken !== 'string' || typeof parsed.refreshToken !== 'string') return null;
    return { accessToken: parsed.accessToken, refreshToken: parsed.refreshToken };
  } catch {
    return null;
  }
}

export type RefreshResult =
  | { kind: 'success'; tokens: TokenPair }
  | { kind: 'http-error'; status: number }
  | { kind: 'network-error' };

export type RefreshDecision =
  | { action: 'apply'; tokens: TokenPair }
  | { action: 'use-current' }
  | { action: 'clear' }
  | { action: 'keep' }
  | { action: 'ignore' };

/** Local session identity only; the JWT signature is still verified by the server. */
export function tokenSessionIdentity(accessToken: string | null): string | null {
  if (!accessToken) return null;
  const payload = accessToken.split('.')[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='));
    const claims = JSON.parse(decoded) as { sub?: unknown; sid?: unknown };
    if (typeof claims.sub !== 'string' || typeof claims.sid !== 'string') return null;
    return `${claims.sub}:${claims.sid}`;
  } catch {
    return null;
  }
}

export function sameTokenSession(left: string | null, right: string | null): boolean {
  const leftIdentity = tokenSessionIdentity(left);
  return leftIdentity !== null && leftIdentity === tokenSessionIdentity(right);
}

/** Decide whether a delayed refresh response may mutate the current local auth session. */
export function decideRefresh(input: {
  sent: TokenPair;
  current: TokenPair | null;
  startEpoch: number;
  currentEpoch: number;
  result: RefreshResult;
}): RefreshDecision {
  const sentIdentity = tokenSessionIdentity(input.sent.accessToken);
  const currentIdentity = tokenSessionIdentity(input.current?.accessToken ?? null);
  const sameSession = sentIdentity !== null && sentIdentity === currentIdentity;
  const epochChanged = input.startEpoch !== input.currentEpoch;
  const tokenChanged = input.current !== null && input.current.refreshToken !== input.sent.refreshToken;

  if (epochChanged) return sameSession && tokenChanged ? { action: 'use-current' } : { action: 'ignore' };

  if (input.result.kind === 'success') {
    if (!sameSession || !input.current) return { action: 'ignore' };
    if (tokenChanged) return { action: 'use-current' };
    if (tokenSessionIdentity(input.result.tokens.accessToken) !== sentIdentity) return { action: 'ignore' };
    return { action: 'apply', tokens: input.result.tokens };
  }

  if (input.result.kind === 'http-error'
      && input.result.status >= 400
      && input.result.status < 500
      && input.result.status !== 408
      && input.result.status !== 429) {
    if (sameSession && tokenChanged) return { action: 'use-current' };
    if (sameSession && input.current?.refreshToken === input.sent.refreshToken) return { action: 'clear' };
    return { action: 'ignore' };
  }

  return { action: 'keep' };
}
